import { useCallback } from "react";
import { QueryParams } from "@router/route-params";
import { useLocation } from "react-router-dom";

export interface PreservedSearchProps {
  /** The pathname or route to navigate to */
  pathname: string;

  /** The (query parameters) that comes after the '?' in the URL as string */
  search: string;
}

/** List of query parameters that should be preserved when navigating between pages */
const PRESERVED_QUERY_PARAMS = [QueryParams.enableBeta, QueryParams.enableDev];

interface Props {
  /** Function to get the preserved search params for a given path */
  preservedSearch(path: string): PreservedSearchProps;
}

/**
 * This hook is used to preserve the search query parameters when navigating to a new page.
 * @param to  The path to navigate to.
 * @returns  An object containing the pathname and search string.
 */
export function usePreservedSearch(): Props {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preservedParams = new URLSearchParams();

  PRESERVED_QUERY_PARAMS.forEach((param) => {
    const value = searchParams.get(param);
    if (value !== null) {
      preservedParams.set(param, value);
    }
  });
  const preservedString = preservedParams.toString();

  const preservedSearch = useCallback(
    (path: string) => {
      return {
        pathname: path.split("?")[0],
        search: preservedString ? `?${preservedString}` : "",
      };
    },
    [preservedString]
  );

  return { preservedSearch };
}
